import Modal from "../../General/ConfirmableModal";
import { useId, useState, type ChangeEvent } from "react";
import axios from "axios";
import {
    Box,
    Button,
    CircularProgress,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import type { ButtonProps } from "@mui/material/Button";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import useAuth from "../../../hooks/useAuth";
import { apiVariable } from "../../../api/apiVariable";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";

interface CompleteMaintenanceOperationButtonProps {
    maintenanceOperationId: string;
    buttonLabel?: string;
    buttonProps?: ButtonProps;
    defaultMileage?: number | string | null;
    invalidateQueryKeys?: QueryKey[];
    onSuccess?: () => void;
}

const MAX_NOTES_LENGTH = 100;

type SnackbarStatus = "idle" | "success" | "error";

const CompleteMaintenanceOperationButton = ({
    maintenanceOperationId,
    buttonLabel = "Complete Maintenance Operation",
    buttonProps,
    defaultMileage,
    invalidateQueryKeys,
    onSuccess,
}: CompleteMaintenanceOperationButtonProps) => {
    const { token } = useAuth();
    const queryClient = useQueryClient();
    const fileInputId = useId();
    const [open, setOpen] = useState(false);
    const [notes, setNotes] = useState("");
    const [file, setFile] = useState<File | null>(null);
    const [dateFulfilled, setDateFulfilled] = useState<Dayjs | null>(dayjs());
    const [mileage, setMileage] = useState<string>("");
    const [status, setStatus] = useState<SnackbarStatus>("idle");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [inputError, setInputError] = useState<string>("");

    const handleOpen = () => {
        if (
            defaultMileage !== undefined &&
            defaultMileage !== null &&
            defaultMileage !== "" &&
            !Number.isNaN(Number(defaultMileage))
        ) {
            setMileage(String(defaultMileage));
        } else {
            setMileage("");
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setNotes("");
        setFile(null);
        setDateFulfilled(dayjs());
        setMileage("");
        setInputError("");
    };

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files[0]) {
            setFile(files[0]);
        }
    };

    const handleSubmit = async () => {
        if (!notes || !file || !dateFulfilled || mileage === "") {
            setStatus("error");
            setErrorMessage("Please fill out all fields");
            return;
        }

        if (notes.length > MAX_NOTES_LENGTH) {
            setInputError(`Notes cannot exceed ${MAX_NOTES_LENGTH} characters.`);
            return;
        }

        const parsedMileage = Number(mileage);
        if (Number.isNaN(parsedMileage)) {
            setStatus("error");
            setErrorMessage("Mileage must be a valid number");
            return;
        }

        if (!token) {
            setStatus("error");
            setErrorMessage("You are not authorized to complete this operation");
            return;
        }

        setLoading(true);
        setStatus("idle");
        setErrorMessage(null);

        try {
            const utcDate = new Date(
                Date.UTC(
                    dateFulfilled.year(),
                    dateFulfilled.month(),
                    dateFulfilled.date()
                )
            );

            const historyPayload = {
                maintenanceOperationId,
                notes,
                dateFullfilled: utcDate,
                mileage: parsedMileage,
            };

            const historyResponse = await axios.post(
                `${apiVariable}/createMaintenanceHistory`,
                historyPayload,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            const formData = new FormData();
            formData.append("file", file);

            await axios.post(
                `${apiVariable}/upload/${historyResponse.data.id}/maintenance`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            if (invalidateQueryKeys?.length) {
                await Promise.all(
                    invalidateQueryKeys.map((queryKey) =>
                        queryClient.invalidateQueries({ queryKey })
                    )
                );
            }

            setStatus("success");
            handleClose();
            onSuccess?.();
        } catch (error: any) {
            const message = error?.response?.data || "Failed to submit incident";
            setStatus("error");
            setErrorMessage(typeof message === "string" ? message : null);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button
                onClick={handleOpen}
                variant={buttonProps?.variant ?? "contained"}
                {...buttonProps}
            >
                {buttonLabel}
            </Button>
            <Modal
                open={open}
                onClose={handleClose}
                sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            >
                <Paper
                    sx={{
                        p: 4,
                        maxWidth: 500,
                        width: "100%",
                        borderRadius: 2,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    }}
                >
                    <Typography
                        variant="h6"
                        component="h2"
                        sx={{ mb: 3, fontWeight: 600 }}
                    >
                        Complete Maintenance Operation
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Date Fulfilled"
                            value={dateFulfilled}
                            onChange={(newValue) => setDateFulfilled(newValue)}
                            slotProps={{
                                textField: { fullWidth: true, sx: { mt: 2 } },
                            }}
                        />
                    </LocalizationProvider>
                    <TextField
                        margin="normal"
                        fullWidth
                        label="Mileage"
                        value={mileage}
                        onChange={(event) => setMileage(event.target.value)}
                        type="number"
                        sx={{ mb: 3 }}
                    />
                    <TextField
                        margin="normal"
                        fullWidth
                        label="Notes"
                        value={notes}
                        onChange={(event) => {
                            setNotes(event.target.value);
                            setInputError("");
                        }}
                        inputProps={{ maxLength: MAX_NOTES_LENGTH }}
                        error={!!inputError}
                        helperText={
                            inputError || `${notes.length}/${MAX_NOTES_LENGTH} characters`
                        }
                        sx={{ mb: 3 }}
                    />
                    <Box sx={{ mb: 3 }}>
                        <input
                            type="file"
                            onChange={handleFileChange}
                            style={{ display: "none" }}
                            id={fileInputId}
                        />
                        <label htmlFor={fileInputId}>
                            <Button
                                variant="outlined"
                                component="span"
                                fullWidth
                                sx={{
                                    textTransform: "none",
                                    borderRadius: 2,
                                }}
                            >
                                Choose File
                            </Button>
                        </label>
                        {file && (
                            <Typography
                                variant="body2"
                                sx={{ mt: 1, color: "text.secondary" }}
                            >
                                Selected file: {file.name}
                            </Typography>
                        )}
                    </Box>
                    <Button
                        onClick={handleSubmit}
                        disabled={loading}
                        variant="contained"
                        fullWidth
                        sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            fontWeight: 500,
                        }}
                    >
                        {loading ? <CircularProgress size={24} /> : "Submit"}
                    </Button>
                </Paper>
            </Modal>
            <StatusSnackbar
                status={status}
                handleClose={() => setStatus("idle")}
                successMessage="Maintenance operation completed successfully"
                errorMessage={errorMessage ?? null}
            />
        </>
    );
};

export default CompleteMaintenanceOperationButton;